import { useState, useEffect } from 'react';
import {
  ShieldCheckIcon,
  CogIcon,
  UserGroupIcon,
  BugAntIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { tasksAPI } from '../../services/api';

// Icon mapping for categories
const categoryIcons = {
  ShieldCheckIcon,
  CogIcon,
  UserGroupIcon,
  BugAntIcon,
  ArrowPathIcon,
};

// Status badge styles
const statusStyles = {
  not_started: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400', label: 'Not Started' },
  in_progress: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400', label: 'In Progress' },
  completed: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400', label: 'Completed' },
  not_applicable: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-500 dark:text-gray-500', label: 'N/A' },
};

// Auto-check status styles
const autoStatusStyles = {
  pass: { icon: CheckCircleIcon, color: 'text-green-500', label: 'Pass' },
  fail: { icon: XCircleIcon, color: 'text-red-500', label: 'Fail' },
  partial: { icon: ExclamationTriangleIcon, color: 'text-yellow-500', label: 'Partial' },
  unknown: { icon: ClockIcon, color: 'text-gray-400', label: 'Not Checked' },
};

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedTask, setExpandedTask] = useState(null);
  const [runningAutoCheck, setRunningAutoCheck] = useState(false);
  const [updatingTask, setUpdatingTask] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const [tasksRes, categoriesRes, summaryRes, teamRes] = await Promise.all([
        tasksAPI.getTasks(),
        tasksAPI.getCategories(),
        tasksAPI.getSummary(),
        tasksAPI.getTeamMembers(),
      ]);

      setTasks(tasksRes.data.data || []);
      setCategories(categoriesRes.data.data || []);
      setSummary(summaryRes.data.data || null);
      setTeamMembers(teamRes.data.data || []);

      // Expand first category by default only on initial load
      if (!silent && categoriesRes.data.data?.length > 0) {
        setExpandedCategories({ [categoriesRes.data.data[0].id]: true });
      }
    } catch (err) {
      if (!silent) setError('Failed to load tasks');
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      setUpdatingTask(taskId);
      // Update local state immediately so user sees the change
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
      await tasksAPI.updateTask(taskId, { status: newStatus });
      // Refresh data in background to get updated summary/progress
      await fetchData({ silent: true });
    } catch (err) {
      console.error('Failed to update task:', err);
      // Revert on error
      await fetchData({ silent: true });
    } finally {
      setUpdatingTask(null);
    }
  };

  const handleNotesChange = async (taskId, notes) => {
    try {
      await tasksAPI.updateTask(taskId, { notes });
    } catch (err) {
      console.error('Failed to update notes:', err);
    }
  };

  const handleAssignTask = async (taskId, userId) => {
    try {
      setUpdatingTask(taskId);
      // Update local state immediately
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, assignedTo: userId || null } : t));
      await tasksAPI.assignTask(taskId, userId || null);
      await fetchData({ silent: true });
    } catch (err) {
      console.error('Failed to assign task:', err);
      await fetchData({ silent: true });
    } finally {
      setUpdatingTask(null);
    }
  };

  const handleRunAutoChecks = async () => {
    try {
      setRunningAutoCheck(true);
      await tasksAPI.runAutoChecks(true);
      await fetchData();
    } catch (err) {
      console.error('Failed to run auto-checks:', err);
    } finally {
      setRunningAutoCheck(false);
    }
  };

  const getTasksByCategory = (categoryId) => {
    return tasks.filter((t) => t.definition?.categoryId === categoryId);
  };

  const getCategoryIcon = (iconName) => {
    return categoryIcons[iconName] || ShieldCheckIcon;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading tasks..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <Button onClick={fetchData} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Cyber Essentials Tasks
          </h1>
          <p className="mt-1 text-slate-600 dark:text-slate-400">
            Track your progress towards Cyber Essentials certification
          </p>
        </div>
        <Button
          onClick={handleRunAutoChecks}
          disabled={runningAutoCheck}
          variant="outline"
        >
          {runningAutoCheck ? (
            <>
              <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Run Auto-Checks
            </>
          )}
        </Button>
      </div>

      {/* Progress Summary */}
      {summary && (
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Overall Progress
              </h2>
              <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {summary.completionPercentage}%
              </span>
            </div>

            {/* Progress bar */}
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-4">
              <div
                className="h-full bg-blue-600 rounded-full transition-all duration-500"
                style={{ width: `${summary.completionPercentage}%` }}
              />
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {summary.completed}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Completed</div>
              </div>
              <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {summary.inProgress}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">In Progress</div>
              </div>
              <div className="text-center p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
                <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                  {summary.notStarted}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Not Started</div>
              </div>
              <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {summary.autoCheck?.pass || 0}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Auto-Pass</div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Category Accordions */}
      <div className="space-y-4">
        {categories.map((category) => {
          const categoryTasks = getTasksByCategory(category.id);
          const completedCount = categoryTasks.filter((t) => t.status === 'completed').length;
          const CategoryIcon = getCategoryIcon(category.icon);
          const isExpanded = expandedCategories[category.id];

          return (
            <Card key={category.id} padding={false}>
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <CategoryIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-slate-900 dark:text-white">
                      {category.name}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {completedCount} of {categoryTasks.length} tasks completed
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {/* Mini progress */}
                  <div className="hidden sm:flex items-center gap-2">
                    <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full"
                        style={{
                          width: `${categoryTasks.length > 0 ? (completedCount / categoryTasks.length) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      {categoryTasks.length > 0
                        ? Math.round((completedCount / categoryTasks.length) * 100)
                        : 0}
                      %
                    </span>
                  </div>
                  {isExpanded ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </button>

              {/* Tasks List */}
              {isExpanded && (
                <div className="border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                  {categoryTasks.map((task) => (
                    <TaskItem
                      key={task.id}
                      task={task}
                      teamMembers={teamMembers}
                      isExpanded={expandedTask === task.id}
                      isUpdating={updatingTask === task.id}
                      onToggle={() => setExpandedTask(expandedTask === task.id ? null : task.id)}
                      onStatusChange={handleStatusChange}
                      onNotesChange={handleNotesChange}
                      onAssign={handleAssignTask}
                    />
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// Task Item Component
function TaskItem({
  task,
  teamMembers,
  isExpanded,
  isUpdating,
  onToggle,
  onStatusChange,
  onNotesChange,
  onAssign,
}) {
  const [notes, setNotes] = useState(task.notes || '');
  const statusStyle = statusStyles[task.status] || statusStyles.not_started;
  const autoStyle = autoStatusStyles[task.autoStatus] || autoStatusStyles.unknown;
  const AutoIcon = autoStyle.icon;

  return (
    <div className="p-4">
      {/* Task Header */}
      <div className="flex items-start gap-3">
        {/* Completion checkbox */}
        <button
          onClick={() =>
            onStatusChange(task.id, task.status === 'completed' ? 'not_started' : 'completed')
          }
          disabled={isUpdating}
          className="mt-0.5 flex-shrink-0"
        >
          {task.status === 'completed' ? (
            <CheckCircleSolidIcon className="h-6 w-6 text-green-500" />
          ) : (
            <div className="h-6 w-6 border-2 border-gray-300 dark:border-gray-600 rounded-full hover:border-green-500 transition-colors" />
          )}
        </button>

        {/* Task content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <button
              onClick={onToggle}
              className="text-left flex-1"
            >
              <h4
                className={`font-medium ${
                  task.status === 'completed'
                    ? 'text-gray-500 dark:text-gray-500 line-through'
                    : 'text-slate-900 dark:text-white'
                }`}
              >
                {task.definition?.title}
              </h4>
            </button>

            {/* Auto-check badge */}
            {task.definition?.autoCheckType && (
              <div
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${autoStyle.color}`}
                title={`Auto-check: ${autoStyle.label}`}
              >
                <AutoIcon className="h-4 w-4" />
                <span className="hidden sm:inline">{autoStyle.label}</span>
              </div>
            )}
          </div>

          {/* Status and assignee row */}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}
            >
              {statusStyle.label}
            </span>

            {task.assignee && (
              <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <UserIcon className="h-3 w-3" />
                {task.assignee.name || task.assignee.email}
              </span>
            )}

            {task.evidence?.length > 0 && (
              <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <DocumentTextIcon className="h-3 w-3" />
                {task.evidence.length} file(s)
              </span>
            )}
          </div>
        </div>

        {/* Expand button */}
        <button onClick={onToggle} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
          {isExpanded ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          )}
        </button>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="mt-4 ml-9 border-t border-slate-200 dark:border-slate-700 pt-4">
          {/* Description & Guidance in styled cards */}
          <div className="grid gap-4 md:grid-cols-2 mb-4">
            {task.definition?.description && (
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 p-4">
                <h5 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  Description
                </h5>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  {task.definition.description}
                </p>
              </div>
            )}

            {task.definition?.guidance && (
              <div className="rounded-lg bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 p-4">
                <h5 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
                  How to Complete
                </h5>
                <ol className="text-sm text-slate-700 dark:text-slate-300 space-y-1.5">
                  {task.definition.guidance.split('\n').map((step, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs flex items-center justify-center font-medium">
                        {i + 1}
                      </span>
                      <span>{step.replace(/^\d+\.\s*/, '')}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          {/* Auto-check data */}
          {task.autoCheckData && (
            <div className="mb-4 rounded-lg bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 p-4">
              <h5 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
                Auto-Check Results
              </h5>
              {task.autoCheckData.percentage !== undefined && (
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${task.autoCheckData.percentage >= 100 ? 'bg-green-500' : task.autoCheckData.percentage >= 80 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min(task.autoCheckData.percentage, 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-slate-900 dark:text-white">
                    {task.autoCheckData.percentage}%
                  </span>
                </div>
              )}
              {task.autoCheckData.message && (
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {task.autoCheckData.message}
                </p>
              )}
            </div>
          )}

          {/* Controls row */}
          <div className="grid gap-4 sm:grid-cols-3">
            {/* Status selector */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                Status
              </label>
              <select
                value={task.status}
                onChange={(e) => onStatusChange(task.id, e.target.value)}
                disabled={isUpdating}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="not_started">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="not_applicable">Not Applicable</option>
              </select>
            </div>

            {/* Assignee selector */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                Assigned To
              </label>
              <select
                value={task.assignedTo || ''}
                onChange={(e) => onAssign(task.id, e.target.value)}
                disabled={isUpdating}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Unassigned</option>
                {teamMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name || member.email}
                  </option>
                ))}
              </select>
            </div>

            {/* Evidence count */}
            {task.definition?.evidenceRequired && (
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                  Evidence
                </label>
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <DocumentTextIcon className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-600 dark:text-slate-300">
                    {task.evidence?.length || 0} file(s) attached
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="mt-4">
            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => {
                if (notes !== task.notes) {
                  onNotesChange(task.id, notes);
                }
              }}
              placeholder="Add notes about this task..."
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              rows={3}
            />
          </div>
        </div>
      )}
    </div>
  );
}
